import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Save, User, Lock, ShieldAlert } from "lucide-react";
import { authAPI, getApiErrorPayload } from "@/lib/api";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { isValidSaudiMobile } from "@/lib/saudi-phone";
import type { User as UserType } from "@/types";

const getPhotoUrl = (photo: string | undefined | null): string | null => {
	if (!photo) return null;
	if (photo.startsWith("http://") || photo.startsWith("https://") || photo.startsWith("data:")) {
		return photo;
	}
	const baseUrl = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace("/api", "");
	return `${baseUrl}${photo.startsWith("/") ? photo : `/${photo}`}`;
};

export default function ProfilePage() {
	const { t } = useTranslation();
	const { updateUser } = useAuth();
	const { toast } = useToast();
	// PermissionRoute sends users here when they open a page their role cannot see.
	const restricted = Boolean((useLocation().state as { restricted?: boolean } | null)?.restricted);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState({
		fullName: "",
		email: "",
		phone: "",
		address: "",
		birthDate: "",
		photo: "" as string,
		currentPassword: "",
		newPassword: "",
		confirmPassword: "",
	});

	useEffect(() => {
		loadProfile();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadProfile = async () => {
		try {
			setLoading(true);
			const data = await authAPI.profile();
			const birthDate =
				typeof data.birthDate === "string"
					? data.birthDate.slice(0, 10)
					: data.birthDate
						? new Date(data.birthDate).toISOString().slice(0, 10)
						: "";
			setForm(prev => ({
				...prev,
				fullName: data.fullName ?? [data.firstName, data.lastName].filter(Boolean).join(" "),
				email: data.email ?? "",
				phone: data.phone ?? "",
				address: data.address ?? "",
				birthDate,
				photo: data.photo ?? data.avatar ?? "",
			}));
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t(
					getApiErrorPayload(error).key,
					(getApiErrorPayload(error).params ?? {}) as Record<string, string>,
				),
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (field: keyof typeof form, value: string) => {
		setForm(prev => ({ ...prev, [field]: value }));
	};

	const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 5 * 1024 * 1024) {
			toast({
				title: t("common.error"),
				description: t("users.errors.fileTooLarge") || "File size must be less than 5MB",
				variant: "destructive",
			});
			return;
		}
		if (!file.type.match(/^image\/(jpg|jpeg|png|gif)$/)) {
			toast({
				title: t("common.error"),
				description: t("users.errors.invalidFileType") || "Only image files are allowed",
				variant: "destructive",
			});
			return;
		}
		try {
			const result = await authAPI.uploadPhoto(file);
			const url = result?.url ?? "";
			setForm(prev => ({ ...prev, photo: url }));
			const updated = await authAPI.profile();
			updateUser(updated as UserType);
			toast({
				title: t("common.success"),
				description: t("users.messages.photoUploaded") || "Photo updated",
			});
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t(
					getApiErrorPayload(error).key,
					(getApiErrorPayload(error).params ?? {}) as Record<string, string>,
				),
				variant: "destructive",
			});
		}
	};

	const handleSubmit = async () => {
		// The mobile is the sign-in identifier, so it can be changed but not cleared.
		if (!form.phone.trim() || !isValidSaudiMobile(form.phone)) {
			toast({
				title: t("common.error"),
				description: t(form.phone.trim() ? "users.errors.phoneInvalid" : "users.errors.phoneRequired"),
				variant: "destructive",
			});
			return;
		}
		if (form.newPassword && form.newPassword !== form.confirmPassword) {
			toast({
				title: t("common.error"),
				description: t("users.errors.passwordMismatch") || "Passwords do not match",
				variant: "destructive",
			});
			return;
		}
		try {
			setSaving(true);
			const payload: Parameters<typeof authAPI.updateProfile>[0] = {
				fullName: form.fullName,
				email: form.email,
				phone: form.phone.trim(),
				address: form.address || undefined,
				birthDate: form.birthDate || undefined,
				photo: form.photo || undefined,
			};
			if (form.newPassword) {
				payload.currentPassword = form.currentPassword;
				payload.newPassword = form.newPassword;
			}
			const updated = await authAPI.updateProfile(payload);
			updateUser(updated as UserType);
			setForm(prev => ({ ...prev, currentPassword: "", newPassword: "", confirmPassword: "" }));
			toast({
				title: t("common.success"),
				description: t("profile.messages.updated") || "Profile updated",
			});
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t(
					getApiErrorPayload(error).key,
					(getApiErrorPayload(error).params ?? {}) as Record<string, string>,
				),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	if (loading) return <Loading />;

	return (
		<div className="container mx-auto py-6 space-y-6 max-w-full">
			{restricted && (
				<div
					role="alert"
					className="flex items-start gap-3 rounded-lg border border-warning/25 bg-warning/10 p-4 text-sm text-warning"
				>
					<ShieldAlert className="h-5 w-5 shrink-0" />
					<p>{t("auth.restrictedNotice")}</p>
				</div>
			)}
			<div className="flex items-center justify-between">
				<div>
					<PageHeading className="">{t("profile.title")}</PageHeading>
					<p className="text-muted-foreground">{t("profile.description")}</p>
				</div>
				<Button onClick={handleSubmit} disabled={saving}>
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2" />
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("common.save")}
				</Button>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<User className="h-5 w-5" />
							{t("users.tabs.personalInfo")}
						</CardTitle>
						<CardDescription>{t("users.form.personalInfoDescription")}</CardDescription>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="space-y-2">
							<Label>{t("users.form.photo")}</Label>
							<div className="flex items-center gap-4">
								{form.photo && getPhotoUrl(form.photo) ? (
									<img
										src={getPhotoUrl(form.photo) ?? form.photo}
										alt={form.fullName}
										className="w-24 h-24 object-cover rounded-lg border"
									/>
								) : (
									<div className="w-24 h-24 rounded-lg border bg-muted flex items-center justify-center">
										<User className="h-12 w-12 text-muted-foreground" />
									</div>
								)}
								<div>
									<Input
										aria-label={t("users.form.photo")}
										type="file"
										accept="image/*"
										onChange={handlePhotoUpload}
										className="w-64"
									/>
									<p className="text-sm text-muted-foreground mt-1">{t("users.form.photoHelp")}</p>
								</div>
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="space-y-2 md:col-span-2">
								<Label htmlFor="fullName">{t("users.form.fullName")}</Label>
								<Input
									id="fullName"
									value={form.fullName}
									onChange={e => handleChange("fullName", e.target.value)}
									placeholder={t("users.form.fullNamePlaceholder")}
								/>
							</div>
							<div className="space-y-2 md:col-span-2">
								<Label htmlFor="email">{t("users.form.email")}</Label>
								<Input
									id="email"
									type="email"
									value={form.email}
									onChange={e => handleChange("email", e.target.value)}
									placeholder={t("users.form.emailPlaceholder")}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="phone">
									{t("users.form.phone")} <span className="text-destructive">*</span>
								</Label>
								<Input
									id="phone"
									value={form.phone}
									onChange={e => handleChange("phone", e.target.value)}
									placeholder={t("users.form.phonePlaceholder")}
									className="rtl:text-right"
									dir="ltr"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="birthDate">{t("users.form.birthDate")}</Label>
								<Input
									id="birthDate"
									type="date"
									value={form.birthDate}
									onChange={e => handleChange("birthDate", e.target.value)}
								/>
							</div>
							<div className="space-y-2 md:col-span-2">
								<Label htmlFor="address">{t("users.form.address")}</Label>
								<Input
									id="address"
									value={form.address}
									onChange={e => handleChange("address", e.target.value)}
									placeholder={t("users.form.addressPlaceholder")}
								/>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<Lock className="h-5 w-5" />
							{t("profile.changePassword")}
						</CardTitle>
						<CardDescription>{t("profile.changePasswordDescription")}</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="currentPassword">{t("profile.currentPassword")}</Label>
							<Input
								id="currentPassword"
								type="password"
								value={form.currentPassword}
								onChange={e => handleChange("currentPassword", e.target.value)}
								placeholder={t("profile.currentPasswordPlaceholder")}
								autoComplete="current-password"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="newPassword">{t("profile.newPassword")}</Label>
							<Input
								id="newPassword"
								type="password"
								value={form.newPassword}
								onChange={e => handleChange("newPassword", e.target.value)}
								placeholder={t("profile.newPasswordPlaceholder")}
								autoComplete="new-password"
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="confirmPassword">{t("profile.confirmPassword")}</Label>
							<Input
								id="confirmPassword"
								type="password"
								value={form.confirmPassword}
								onChange={e => handleChange("confirmPassword", e.target.value)}
								placeholder={t("profile.confirmPasswordPlaceholder")}
								autoComplete="new-password"
							/>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
